/**
 * Browser OTel tracing (loaded lazily — only reached when the build-time
 * flag VUE_APP_OTEL_TRACING === 'on'; see src/tracing/loader.js).
 *
 * - WebTracerProvider with a session-based 10% sampler: the sampled/not
 *   decision is drawn once per session (page load) so a traced user is
 *   fully traced, an untraced user emits nothing.
 * - Fetch instrumentation: spans for every fetch except the OTLP export
 *   itself (ignored to avoid an export->span->export loop), and W3C
 *   traceparent propagation to the configured API origin. Cross-origin
 *   fetches only receive traceparent when the URL matches
 *   `propagateTraceHeaderCorsUrls` (see the instrumentation's
 *   shouldPropagateTraceHeaders), which we set to the API origin.
 * - Exports OTLP/JSON over fetch to the configured collector URL.
 */

import {
  WebTracerProvider,
  BatchSpanProcessor,
  SamplingDecision,
} from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';

// Session-based sampling: decide once whether this session is traced.
// `rng` is injectable for deterministic tests.
function sessionSampler(ratio, rng) {
  const random = rng || Math.random;
  const sampled = random() < ratio;
  return {
    shouldSample: function () {
      return {
        decision: sampled ? SamplingDecision.RECORD_AND_SAMPLED : SamplingDecision.NOT_RECORD,
      };
    },
    toString: function () {
      return 'SessionRatioBased{' + ratio + ',' + sampled + '}';
    },
  };
}

// OTel URL matchers compare strings by exact equality, so build anchored
// regexes to match every request under an origin/path prefix.
function urlPrefixRegex(url) {
  const escaped = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\/$/, '');
  return new RegExp('^' + escaped);
}

let initialized = false;

function initTracing(config, overrides) {
  if (initialized) {
    return;
  }
  initialized = true;
  const opts = overrides || {};

  const exporter = opts.exporter || new OTLPTraceExporter({ url: config.exporterUrl });
  const processor = opts.spanProcessor || new BatchSpanProcessor(exporter);

  const provider = new WebTracerProvider({
    sampler: opts.sampler || sessionSampler(config.sampleRatio),
    spanProcessors: [processor],
    resource: resourceFromAttributes({
      'service.name': config.serviceName,
    }),
  });

  provider.register({
    contextManager: opts.contextManager || new ZoneContextManager(),
    propagator: new W3CTraceContextPropagator(),
  });

  const apiOrigin = config.apiOrigin.replace(/\/$/, '');
  const instrumentation = new FetchInstrumentation({
    // Propagate traceparent to API calls (cross-origin requires this list).
    propagateTraceHeaderCorsUrls: [urlPrefixRegex(apiOrigin)],
    // Never span/trace our own telemetry export.
    ignoreUrls: [urlPrefixRegex(config.exporterUrl)],
  });
  registerInstrumentations({
    instrumentations: [instrumentation],
  });
}

export { initTracing, sessionSampler };
